import React, { useState, useRef, useEffect } from "react";
import MerakiSDK, { Exceptions } from "@urbanpiper-engineering/meraki-sdk";
import { useTranslation } from "react-i18next";
import { getServerSideProps as getInitialProps } from "../../hooks/server.props";
import { Wrapper } from "@googlemaps/react-wrapper";
import {
  StoreLocatorCardMobile,
  Drawer,
  Divider,
  StoreLocatorSearch,
  GoogleMap,
  Typography,
  StoreLocatorList,
} from "@urbanpiper-engineering/meraki-components";
import useConfirm from "@urbanpiper-engineering/meraki-components/dist/Molecules/Confirm";
import useAlert from "@urbanpiper-engineering/meraki-components/dist/Molecules/Alert";
import styles from "./stores.module.scss";
import configSelector from "../../external/configSelector";
import { StoreState } from "@urbanpiper-engineering/meraki-sdk/dist/esm/stores/states/store.state";
import { useRouter } from "next/router";
import { CloseOutlined } from "@ant-design/icons";
import pageSelector from "../../external/pageSelector";
import Head from "next/head";

export const getServerSideProps = async (props: any) => {
  const initProps = await getInitialProps(props);
  const mapKey = configSelector.getGoogleMapsKey();
  const seoConfig = pageSelector.getStoreLocatorPage()?.seo[props.locale] || {};
  const primaryColor = configSelector.getPrimaryColor();

  return {
    props: {
      ...initProps.props,
      mapKey,
      seoConfig,
      primaryColor,
    },
  };
};

function StoresPage({ mapKey, seoConfig, primaryColor, businessInfo }: any) {
  const { t } = useTranslation();
  const router = useRouter();
  const [storeList, setStoreList] = useState([]);
  const useStoreState = MerakiSDK.getStoreState().hook();
  const confirmDialog = useConfirm();
  const alertDialog = useAlert();
  const selectedStore = useStoreState(
    (state: typeof StoreState) => state.selectedStore,
  );
  const [isStoreDrawerVisible, setIsStoreDrawerVisible] =
    useState<boolean>(false);
  const [filteredListMobile, setFilteredListMobile] = useState<any>([]);
  const [filteredListWeb, setFilteredListWeb] = useState<any>(storeList);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    (async () => {
      const stores = await MerakiSDK.getStores();
      const storesEnabledForOrdering = stores?.filter(
        (store: any) => store?.enabled_for_ordering,
      );
      setStoreList(storesEnabledForOrdering);
      setFilteredListWeb(storesEnabledForOrdering);
    })();
  }, []);

  const handleSearchStore = async (searchTerm: string) => {
    if (searchTerm !== "") {
      const filteredList = await MerakiSDK.searchStore(storeList, searchTerm);
      setFilteredListMobile(filteredList);
      setFilteredListWeb(filteredList);
      if (searchRef && searchRef.current) {
        searchRef.current.scrollIntoView();
      }
    }
  };

  const handleSelectStoreError = (err: any) => {
    if (err.message === Exceptions.STORE_ORDERING_NOT_ENABLED) {
      alertDialog({
        title: t("storeLocator.storeClosed"),
        content:
          businessInfo?.msg_nearest_store_closed ||
          t("storeLocator.storeClosed"),
      });
    }
    if (err.message === Exceptions.STORE_TEMPORARY_CLOSED) {
      alertDialog({
        title: t("storeLocator.storeClosed"),
        content:
          businessInfo?.msg_nearest_store_closed ||
          t("storeLocator.storeClosed"),
      });
    }
  };

  const handleSelectStore = (storeId: number) => {
    const selectedStore = storeList.find((s: any) => s.id === storeId);
    if (selectedStore) {
      const storeContent = {
        id: storeId,
        address: selectedStore.address,
        latitude: selectedStore.lat,
        longitude: selectedStore.lng,
        storeInfo: selectedStore,
        ffMode: "pickup",
      };

      try {
        MerakiSDK.setSelectedStore(storeContent);
        router.push("/menu");
      } catch (err: any) {
        if (err.message === Exceptions.CART_ALREADY_EXISTS) {
          confirmDialog({
            title: t("header.clearCartHeading"),
            content: t("header.clearCartDescription"),
            onConfirm: () => {
              try {
                MerakiSDK.setSelectedStore(storeContent, true);
                router.push("/menu");
              } catch (err: any) {
                handleSelectStoreError(err);
              }
            },
          });
        }
        handleSelectStoreError(err);
      }
    }
  };

  return (
    <div className={styles.parent}>
      <Head>
        <title>{seoConfig.title}</title>
        <meta name="description" content={seoConfig.description} />
        <meta name="keywords" content={seoConfig.keywords} />
      </Head>
      <div className={`container ${styles.web_container}`}>
        <div className={styles.map_main}>
          <Wrapper apiKey={mapKey}>
            <GoogleMap
              className={styles.map}
              center={{
                lat: selectedStore?.latitude || storeList?.[0]?.latitude,
                lng: selectedStore?.longitude || storeList?.[0]?.longitude,
              }}
              zoom={13}
              markers={storeList.map((store: any) => ({
                lat: store.lat,
                lng: store.lng,
                label: store.name,
                color: primaryColor,
              }))}
            />
          </Wrapper>
        </div>
        <div className={styles.stores}>
          <Typography variant="headingOne" classes={styles.typo}>
            {t("storeLocator.sectionHeading")}
          </Typography>
          <StoreLocatorList
            classes={styles.web_store_list}
            stores={filteredListWeb}
            searchStore={handleSearchStore}
            selectStore={(storeId: number) => handleSelectStore(storeId)}
          />
        </div>
      </div>
      <div className={styles.mobile_container}>
        <Wrapper apiKey={mapKey}>
          <GoogleMap
            className={styles.mobile_map}
            center={{
              lat: selectedStore?.latitude || storeList?.[0]?.latitude,
              lng: selectedStore?.longitude || storeList?.[0]?.longitude,
            }}
            zoom={13}
            markers={storeList.map((store: any) => ({
              lat: store.lat,
              lng: store.lng,
              label: store.name,
              color: primaryColor,
            }))}
          />
        </Wrapper>

        <div ref={searchRef}>
          <StoreLocatorSearch
            searchStore={handleSearchStore}
            selectStore={(storeId: number) => handleSelectStore(storeId)}
            defaultSuggestion={storeList?.[0]}
            stores={filteredListMobile}
          />
        </div>
      </div>
      <Drawer
        classes={styles.mobile_stores_drawer}
        open={isStoreDrawerVisible}
        direction={"right"}
        onClose={() => setIsStoreDrawerVisible(false)}
      >
        <div className={styles.mobile_stores_drawer_header}>
          <Typography
            variant="headingTwo"
            classes={styles.mobile_stores_drawer_header_title}
          >
            {t("storeLocator.sectionHeading")}
          </Typography>
          <CloseOutlined onClick={() => setIsStoreDrawerVisible(false)} />
        </div>
        {storeList.map((store: any) => (
          <React.Fragment key={store.id}>
            <StoreLocatorCardMobile
              key={store.id}
              {...store}
              selectStore={() => {
                MerakiSDK.setSelectedStore(
                  {
                    id: store.id,
                    address: store.address,
                    latitude: store.lat,
                    longitude: store.lng,
                    ffMode: "pickup",
                  },
                  true,
                );
                router.push("/menu");
              }}
            />
            <Divider
              dividerStyle={"solid"}
              variant={"horizontal"}
              size={"100%"}
            />
          </React.Fragment>
        ))}
      </Drawer>
    </div>
  );
}

export default StoresPage;
